import { type ThirdPartyConfig } from '../../types/schemas/types';
import { ProductKeys3P } from './types';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { DO_NOT_IMPORT_ProductKeys3P } from './do-not-import-product-keys3-p';

type ConfigImportFactory = () => Promise<ThirdPartyConfig>;

/**
 * An async Map-like registry that lazily loads third-party configs via dynamic import().
 * Each config module is only fetched when first accessed, enabling true code-splitting.
 * Results are cached after first resolution.
 */
export class AsyncThirdPartyConfigMap {
	private factories: Map<ProductKeys3P, ConfigImportFactory>;
	private cache: Map<ProductKeys3P, ThirdPartyConfig> = new Map();

	constructor(entries: [ProductKeys3P, ConfigImportFactory][]) {
		this.factories = new Map(entries);
	}

	/**
	 * Returns the number of registered configs.
	 */
	get size(): number {
		return this.factories.size;
	}

	/**
	 * Checks whether a config is registered for the given key.
	 */
	has(key: ProductKeys3P): boolean {
		return this.factories.has(key);
	}

	/**
	 * Returns all registered product keys synchronously.
	 */
	keys(): ProductKeys3P[] {
		return Array.from(this.factories.keys());
	}

	/**
	 * Asynchronously retrieves a single config by key.
	 * The config module is loaded on first access and cached thereafter.
	 */
	async get(key: ProductKeys3P): Promise<ThirdPartyConfig | undefined> {
		if (this.cache.has(key)) {
			return this.cache.get(key);
		}
		const factory = this.factories.get(key);
		if (!factory) {
			return undefined;
		}
		const config = await factory();
		this.cache.set(key, config);
		return config;
	}

	/**
	 * Asynchronously iterates over all entries, invoking the callback for each.
	 * Each config is lazily loaded as the iteration reaches it.
	 */
	async forEach(
		callbackfn: (value: ThirdPartyConfig, key: ProductKeys3P) => void | Promise<void>,
	): Promise<void> {
		for (const key of this.factories.keys()) {
			const config = await this.get(key);
			if (config) {
				await callbackfn(config, key);
			}
		}
	}

	/**
	 * Async generator that yields [key, config] entries lazily.
	 */
	async *entries(): AsyncIterableIterator<[ProductKeys3P, ThirdPartyConfig]> {
		for (const key of this.factories.keys()) {
			const config = await this.get(key);
			if (config) {
				yield [key, config];
			}
		}
	}

	/**
	 * Async generator that yields config values lazily.
	 */
	async *values(): AsyncIterableIterator<ThirdPartyConfig> {
		for (const key of this.factories.keys()) {
			const config = await this.get(key);
			if (config) {
				yield config;
			}
		}
	}

	/**
	 * Resolves all configs and returns them as a standard Map.
	 * Useful when all configs are needed at once.
	 */
	async getAll(): Promise<Map<ProductKeys3P, ThirdPartyConfig>> {
		const result = new Map<ProductKeys3P, ThirdPartyConfig>();
		for (const key of this.factories.keys()) {
			const config = await this.get(key);
			if (config) {
				result.set(key, config);
			}
		}
		return result;
	}
}

/**
 * Registry mapping ProductKeys3P to their configurations.
 * This is the central lookup for all third-party connector configs.
 *
 * Configs are lazily loaded via dynamic import() — each module is only
 * fetched when the entry is first accessed and cached thereafter.
 */
export const ThirdPartyConfigs: AsyncThirdPartyConfigMap = new AsyncThirdPartyConfigMap([
	[
		ProductKeys3P.AtlassianWebCrawler,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-atlassian-web-crawler" */ './atlassian-web-crawler'
			).then((m) => m.atlassianWebCrawlerConfig),
	],
	[
		ProductKeys3P.Figma,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-figma" */ './figma').then(
				(m) => m.figmaConfig,
			),
	],
	[
		ProductKeys3P.Github,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-github" */ './github').then(
				(m) => m.githubConfig,
			),
	],
	[
		ProductKeys3P.GoogleDrive,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-google" */ './google').then(
				(m) => m.googleDriveConfig,
			),
	],
	[
		ProductKeys3P.Sharepoint,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-sharepoint" */ './sharepoint').then(
				(m) => m.sharepointConfig,
			),
	],
	[
		ProductKeys3P.Loom,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-loom" */ './loom').then(
				(m) => m.loomConfig,
			),
	],
	[
		ProductKeys3P.OneDrive,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-onedrive" */ './onedrive').then(
				(m) => m.oneDriveConfig,
			),
	],
	[
		ProductKeys3P.Dropbox,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-dropbox" */ './dropbox').then(
				(m) => m.dropboxConfig,
			),
	],
	[
		ProductKeys3P.Slack,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-slack" */ './slack').then(
				(m) => m.slackConfig,
			),
	],
	[
		ProductKeys3P.Teams,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-teams" */ './teams').then(
				(m) => m.teamsConfig,
			),
	],
	[
		ProductKeys3P.GoogleCalendar,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-google-calendar" */ './google-calendar'
			).then((m) => m.googleCalendarConfig),
	],
	[
		ProductKeys3P.Gmail,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-gmail" */ './gmail').then(
				(m) => m.gmailConfig,
			),
	],
	[
		ProductKeys3P.OutlookCalendar,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-outlook-calendar" */ './outlook-calendar'
			).then((m) => m.outlookCalendarConfig),
	],
	[
		ProductKeys3P.OutlookMail,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-outlook-mail" */ './outlook-mail'
			).then((m) => m.outlookMail),
	],
	[
		ProductKeys3P.Asana,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-asana" */ './asana').then(
				(m) => m.asanaConfig,
			),
	],
	[
		ProductKeys3P.Zendesk,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-zendesk" */ './zendesk').then(
				(m) => m.zendeskConfig,
			),
	],
	[
		ProductKeys3P.Airtable,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-airtable" */ './airtable').then(
				(m) => m.airtableConfig,
			),
	],
	[
		ProductKeys3P.Smartsheet,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-smartsheet" */ './smartsheet').then(
				(m) => m.smartsheetConfig,
			),
	],
	[
		ProductKeys3P.Notion,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-notion" */ './notion').then(
				(m) => m.notionConfig,
			),
	],
	[
		ProductKeys3P.Miro,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-miro" */ './miro').then(
				(m) => m.miroConfig,
			),
	],
	[
		ProductKeys3P.Docusign,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-docusign" */ './docusign').then(
				(m) => m.docusignConfig,
			),
	],
	[
		ProductKeys3P.Lucid,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-lucid" */ './lucid').then(
				(m) => m.lucidConfig,
			),
	],
	[
		ProductKeys3P.Monday,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-monday" */ './monday').then(
				(m) => m.mondayConfig,
			),
	],
	[
		ProductKeys3P.AzureDevops,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-azure-devops" */ './azure-devops'
			).then((m) => m.azureDevopsConfig),
	],
	[
		ProductKeys3P.Gitlab,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-gitlab" */ './gitlab').then(
				(m) => m.gitlabConfig,
			),
	],
	[
		ProductKeys3P.ConfluenceDC,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-confluence-dc" */ './confluence-dc'
			).then((m) => m.confluenceDCConfig),
	],
	[
		ProductKeys3P.JiraDC,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-jira-dc" */ './jira-dc').then(
				(m) => m.jiraDCConfig,
			),
	],
	[
		ProductKeys3P.JsmDC,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-jsm-dc" */ './jsm-dc').then(
				(m) => m.jsmDCConfig,
			),
	],
	[
		ProductKeys3P.Canva,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-canva" */ './canva').then(
				(m) => m.canvaConfig,
			),
	],
	[
		ProductKeys3P.Box,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-box" */ './box').then(
				(m) => m.boxConfig,
			),
	],
	[
		ProductKeys3P.Zoom,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-zoom" */ './zoom').then(
				(m) => m.zoomConfig,
			),
	],
	[
		ProductKeys3P.Zeplin,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-zeplin" */ './zeplin').then(
				(m) => m.zeplinConfig,
			),
	],
	[
		ProductKeys3P.ServiceNow,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-servicenow" */ './servicenow').then(
				(m) => m.serviceNowConfig,
			),
	],
	[
		ProductKeys3P.Freshservice,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-freshservice" */ './freshservice'
			).then((m) => m.freshserviceConfig),
	],
	[
		ProductKeys3P.PowerBI,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-powerBI" */ './powerBI').then(
				(m) => m.powerBIConfig,
			),
	],
	[
		ProductKeys3P.ClickUp,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-clickup" */ './clickup').then(
				(m) => m.clickUpConfig,
			),
	],
	[
		ProductKeys3P.Amplitude,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-amplitude" */ './amplitude').then(
				(m) => m.amplitudeConfig,
			),
	],
	[
		ProductKeys3P.Stripe,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-stripe" */ './stripe').then(
				(m) => m.stripeConfig,
			),
	],
	[
		ProductKeys3P.AdobeXD,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-adobe-xd" */ './adobe-xd').then(
				(m) => m.adobeXDConfig,
			),
	],
	[
		ProductKeys3P.Aha,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-aha" */ './aha').then(
				(m) => m.ahaConfig,
			),
	],
	[
		ProductKeys3P.AdobeSign,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-adobe-sign" */ './adobe-sign').then(
				(m) => m.adobeSignConfig,
			),
	],
	[
		ProductKeys3P.Mural,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-mural" */ './mural').then(
				(m) => m.muralConfig,
			),
	],
	[
		ProductKeys3P.Webex,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-webex" */ './webex').then(
				(m) => m.webexConfig,
			),
	],
	[
		ProductKeys3P.Todoist,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-todoist" */ './todoist').then(
				(m) => m.todoistConfig,
			),
	],
	[
		ProductKeys3P.PagerDuty,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-pagerduty" */ './pagerduty').then(
				(m) => m.pagerDutyConfig,
			),
	],
	[
		ProductKeys3P.Pipedrive,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-pipedrive" */ './pipedrive').then(
				(m) => m.pipedriveConfig,
			),
	],
	[
		ProductKeys3P.Hubspot,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-hubspot" */ './hubspot').then(
				(m) => m.hubSpotConfig,
			),
	],
	[
		ProductKeys3P.Dovetail,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-dovetail" */ './dovetail').then(
				(m) => m.dovetailConfig,
			),
	],
	[
		ProductKeys3P.LaunchDarkly,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-launchdarkly" */ './launchdarkly'
			).then((m) => m.launchDarklyConfig),
	],
	[
		ProductKeys3P.Salesforce,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-salesforce" */ './salesforce').then(
				(m) => m.salesforceConfig,
			),
	],
	[
		ProductKeys3P.Sentry,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-sentry" */ './sentry').then(
				(m) => m.sentryConfig,
			),
	],
	[
		ProductKeys3P.Gitbook,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-gitbook" */ './gitbook').then(
				(m) => m.gitbookConfig,
			),
	],
	[
		ProductKeys3P.AWSS3,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-aws-s3" */ './aws-s3').then(
				(m) => m.awss3Config,
			),
	],
	[
		ProductKeys3P.Databricks,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-databricks" */ './databricks').then(
				(m) => m.databricksConfig,
			),
	],
	[
		ProductKeys3P.ForgeAdamVitareqDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-adam-vitareq-dev" */ './forge-adam-vitareq-dev'
			).then((m) => m.forgeAdamVitareqConfig),
	],
	[
		ProductKeys3P.ForgeAdamVitareqProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-adam-vitareq-prod" */ './forge-adam-vitareq-prod'
			).then((m) => m.forgeAdamVitareqConfig),
	],
	[
		ProductKeys3P.ForgeK15tScrollSitesDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-k15t-scroll-sites-dev" */ './forge-k15t-scroll-sites-dev'
			).then((m) => m.forgeK15tScrollSitesConfig),
	],
	[
		ProductKeys3P.ForgeK15tScrollSitesProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-k15t-scroll-sites-prod" */ './forge-k15t-scroll-sites-prod'
			).then((m) => m.forgeK15tScrollSitesConfig),
	],
	[
		ProductKeys3P.ForgePioAwsDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-aws-dev" */ './forge-pio-aws-dev'
			).then((m) => m.forgePioAwsConfig),
	],
	[
		ProductKeys3P.ForgePioAwsProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-aws-prod" */ './forge-pio-aws-prod'
			).then((m) => m.forgePioAwsConfig),
	],
	[
		ProductKeys3P.ForgePioClickupDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-clickup-dev" */ './forge-pio-clickup-dev'
			).then((m) => m.forgePioClickupConfig),
	],
	[
		ProductKeys3P.ForgePioClickupProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-clickup-prod" */ './forge-pio-clickup-prod'
			).then((m) => m.forgePioClickupConfig),
	],
	[
		ProductKeys3P.ForgePioGitbookDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-gitbook-dev" */ './forge-pio-gitbook-dev'
			).then((m) => m.forgePioGitbookConfig),
	],
	[
		ProductKeys3P.ForgePioGitbookProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-gitbook-prod" */ './forge-pio-gitbook-prod'
			).then((m) => m.forgePioGitbookConfig),
	],
	[
		ProductKeys3P.ForgePioGithubDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-github-dev" */ './forge-pio-github-dev'
			).then((m) => m.forgePioGithubConfig),
	],
	[
		ProductKeys3P.ForgePioGithubProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-github-prod" */ './forge-pio-github-prod'
			).then((m) => m.forgePioGithubConfig),
	],
	[
		ProductKeys3P.ForgePioGitlabDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-gitlab-dev" */ './forge-pio-gitlab-dev'
			).then((m) => m.forgePioGitlabConfig),
	],
	[
		ProductKeys3P.ForgePioGitlabProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-gitlab-prod" */ './forge-pio-gitlab-prod'
			).then((m) => m.forgePioGitlabConfig),
	],
	[
		ProductKeys3P.ForgePioGoogleDriveDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-google-drive-dev" */ './forge-pio-google-drive-dev'
			).then((m) => m.forgePioGoogleDriveConfig),
	],
	[
		ProductKeys3P.ForgePioGoogleDriveProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-google-drive-prod" */ './forge-pio-google-drive-prod'
			).then((m) => m.forgePioGoogleDriveConfig),
	],
	[
		ProductKeys3P.ForgePioGuruDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-guru-dev" */ './forge-pio-guru-dev'
			).then((m) => m.forgePioGuruConfig),
	],
	[
		ProductKeys3P.ForgePioGuruProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-guru-prod" */ './forge-pio-guru-prod'
			).then((m) => m.forgePioGuruConfig),
	],
	[
		ProductKeys3P.ForgePioHelpjuiceDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-helpjuice-dev" */ './forge-pio-helpjuice-dev'
			).then((m) => m.forgePioHelpjuiceConfig),
	],
	[
		ProductKeys3P.ForgePioHelpjuiceProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-helpjuice-prod" */ './forge-pio-helpjuice-prod'
			).then((m) => m.forgePioHelpjuiceConfig),
	],
	[
		ProductKeys3P.ForgePioHubspotDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-hubspot-dev" */ './forge-pio-hubspot-dev'
			).then((m) => m.forgePioHubspotConfig),
	],
	[
		ProductKeys3P.ForgePioHubspotProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-hubspot-prod" */ './forge-pio-hubspot-prod'
			).then((m) => m.forgePioHubspotConfig),
	],
	[
		ProductKeys3P.ForgePioSalesforceDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-salesforce-dev" */ './forge-pio-salesforce-dev'
			).then((m) => m.forgePioSalesforceConfig),
	],
	[
		ProductKeys3P.ForgePioSalesforceProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-salesforce-prod" */ './forge-pio-salesforce-prod'
			).then((m) => m.forgePioSalesforceConfig),
	],
	[
		ProductKeys3P.ForgePioSharepointDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-sharepoint-dev" */ './forge-pio-sharepoint-dev'
			).then((m) => m.forgePioSharepointConfig),
	],
	[
		ProductKeys3P.ForgePioSharepointProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-sharepoint-prod" */ './forge-pio-sharepoint-prod'
			).then((m) => m.forgePioSharepointConfig),
	],
	[
		ProductKeys3P.ForgePioStackOverflowDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-stack-overflow-dev" */ './forge-pio-stack-overflow-dev'
			).then((m) => m.forgePioStackOverflowConfig),
	],
	[
		ProductKeys3P.ForgePioStackOverflowProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-stack-overflow-prod" */ './forge-pio-stack-overflow-prod'
			).then((m) => m.forgePioStackOverflowConfig),
	],
	[
		ProductKeys3P.ForgeUtoolityGitbookDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-utoolity-gitbook-dev" */ './forge-utoolity-gitbook-dev'
			).then((m) => m.forgeUtoolityGitbookConfig),
	],
	[
		ProductKeys3P.ForgeUtoolityGitbookProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-utoolity-gitbook-prod" */ './forge-utoolity-gitbook-prod'
			).then((m) => m.forgeUtoolityGitbookConfig),
	],
	[
		ProductKeys3P.ForgeGdriveEugeneTestDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-gdrive-eugene-test-dev" */ './forge-gdrive-eugene-test-dev'
			).then((m) => m.forgeGdriveEugeneTestDevConfig),
	],
	[
		ProductKeys3P.ForgeGdriveEugeneTestProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-gdrive-eugene-test-prod" */ './forge-gdrive-eugene-test-prod'
			).then((m) => m.forgeGdriveEugeneTestProdConfig),
	],
	[
		ProductKeys3P.ForgeUnitoSalesforceDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-salesforce-dev" */ './forge-unito-salesforce-dev'
			).then((m) => m.forgeUnitoSalesforceConfig),
	],
	[
		ProductKeys3P.ForgeUnitoSalesforceProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-salesforce-prod" */ './forge-unito-salesforce-prod'
			).then((m) => m.forgeUnitoSalesforceConfig),
	],
	[
		ProductKeys3P.ForgeUnitoServicenowDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-servicenow-dev" */ './forge-unito-servicenow-dev'
			).then((m) => m.forgeUnitoServicenowConfig),
	],
	[
		ProductKeys3P.ForgeUnitoServicenowProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-servicenow-prod" */ './forge-unito-servicenow-prod'
			).then((m) => m.forgeUnitoServicenowConfig),
	],
	[
		ProductKeys3P.ForgeUnitoWrikeDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-wrike-dev" */ './forge-unito-wrike-dev'
			).then((m) => m.forgeUnitoWrikeConfig),
	],
	[
		ProductKeys3P.ForgeUnitoWrikeProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-wrike-prod" */ './forge-unito-wrike-prod'
			).then((m) => m.forgeUnitoWrikeConfig),
	],
	[
		ProductKeys3P.ForgeUnitoAsanaDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-asana-dev" */ './forge-unito-asana-dev'
			).then((m) => m.forgeUnitoAsanaConfig),
	],
	[
		ProductKeys3P.ForgeUnitoAsanaProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-asana-prod" */ './forge-unito-asana-prod'
			).then((m) => m.forgeUnitoAsanaConfig),
	],
	[
		ProductKeys3P.ForgeUnitoBasecampDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-basecamp-dev" */ './forge-unito-basecamp-dev'
			).then((m) => m.forgeUnitoBasecampConfig),
	],
	[
		ProductKeys3P.ForgeUnitoBasecampProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-basecamp-prod" */ './forge-unito-basecamp-prod'
			).then((m) => m.forgeUnitoBasecampConfig),
	],
	[
		ProductKeys3P.AtlassianWebCrawler,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-atlassian-web-crawler" */ './atlassian-web-crawler'
			).then((m) => m.atlassianWebCrawlerConfig),
	],
	[
		ProductKeys3P.Figma,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-figma" */ './figma').then(
				(m) => m.figmaConfig,
			),
	],
	[
		ProductKeys3P.Github,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-github" */ './github').then(
				(m) => m.githubConfig,
			),
	],
	[
		ProductKeys3P.GoogleDrive,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-google" */ './google').then(
				(m) => m.googleDriveConfig,
			),
	],
	[
		ProductKeys3P.Sharepoint,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-sharepoint" */ './sharepoint').then(
				(m) => m.sharepointConfig,
			),
	],
	[
		ProductKeys3P.Loom,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-loom" */ './loom').then(
				(m) => m.loomConfig,
			),
	],
	[
		ProductKeys3P.OneDrive,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-onedrive" */ './onedrive').then(
				(m) => m.oneDriveConfig,
			),
	],
	[
		ProductKeys3P.Dropbox,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-dropbox" */ './dropbox').then(
				(m) => m.dropboxConfig,
			),
	],
	[
		ProductKeys3P.Slack,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-slack" */ './slack').then(
				(m) => m.slackConfig,
			),
	],
	[
		ProductKeys3P.Teams,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-teams" */ './teams').then(
				(m) => m.teamsConfig,
			),
	],
	[
		ProductKeys3P.GoogleCalendar,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-google-calendar" */ './google-calendar'
			).then((m) => m.googleCalendarConfig),
	],
	[
		ProductKeys3P.Gmail,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-gmail" */ './gmail').then(
				(m) => m.gmailConfig,
			),
	],
	[
		ProductKeys3P.OutlookCalendar,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-outlook-calendar" */ './outlook-calendar'
			).then((m) => m.outlookCalendarConfig),
	],
	[
		ProductKeys3P.OutlookMail,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-outlook-mail" */ './outlook-mail'
			).then((m) => m.outlookMail),
	],
	[
		ProductKeys3P.Asana,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-asana" */ './asana').then(
				(m) => m.asanaConfig,
			),
	],
	[
		ProductKeys3P.Zendesk,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-zendesk" */ './zendesk').then(
				(m) => m.zendeskConfig,
			),
	],
	[
		ProductKeys3P.Airtable,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-airtable" */ './airtable').then(
				(m) => m.airtableConfig,
			),
	],
	[
		ProductKeys3P.Smartsheet,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-smartsheet" */ './smartsheet').then(
				(m) => m.smartsheetConfig,
			),
	],
	[
		ProductKeys3P.Notion,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-notion" */ './notion').then(
				(m) => m.notionConfig,
			),
	],
	[
		ProductKeys3P.Miro,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-miro" */ './miro').then(
				(m) => m.miroConfig,
			),
	],
	[
		ProductKeys3P.Docusign,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-docusign" */ './docusign').then(
				(m) => m.docusignConfig,
			),
	],
	[
		ProductKeys3P.Lucid,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-lucid" */ './lucid').then(
				(m) => m.lucidConfig,
			),
	],
	[
		ProductKeys3P.Monday,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-monday" */ './monday').then(
				(m) => m.mondayConfig,
			),
	],
	[
		ProductKeys3P.AzureDevops,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-azure-devops" */ './azure-devops'
			).then((m) => m.azureDevopsConfig),
	],
	[
		ProductKeys3P.Gitlab,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-gitlab" */ './gitlab').then(
				(m) => m.gitlabConfig,
			),
	],
	[
		ProductKeys3P.ConfluenceDC,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-confluence-dc" */ './confluence-dc'
			).then((m) => m.confluenceDCConfig),
	],
	[
		ProductKeys3P.JiraDC,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-jira-dc" */ './jira-dc').then(
				(m) => m.jiraDCConfig,
			),
	],
	[
		ProductKeys3P.JsmDC,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-jsm-dc" */ './jsm-dc').then(
				(m) => m.jsmDCConfig,
			),
	],
	[
		ProductKeys3P.Canva,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-canva" */ './canva').then(
				(m) => m.canvaConfig,
			),
	],
	[
		ProductKeys3P.Box,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-box" */ './box').then(
				(m) => m.boxConfig,
			),
	],
	[
		ProductKeys3P.Gong,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-gong" */ './gong').then(
				(m) => m.gongConfig,
			),
	],
	[
		ProductKeys3P.Zoom,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-zoom" */ './zoom').then(
				(m) => m.zoomConfig,
			),
	],
	[
		ProductKeys3P.Zeplin,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-zeplin" */ './zeplin').then(
				(m) => m.zeplinConfig,
			),
	],
	[
		ProductKeys3P.ServiceNow,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-servicenow" */ './servicenow').then(
				(m) => m.serviceNowConfig,
			),
	],
	[
		ProductKeys3P.Freshservice,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-freshservice" */ './freshservice'
			).then((m) => m.freshserviceConfig),
	],
	[
		ProductKeys3P.PowerBI,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-powerBI" */ './powerBI').then(
				(m) => m.powerBIConfig,
			),
	],
	[
		ProductKeys3P.ClickUp,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-clickup" */ './clickup').then(
				(m) => m.clickUpConfig,
			),
	],
	[
		ProductKeys3P.Amplitude,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-amplitude" */ './amplitude').then(
				(m) => m.amplitudeConfig,
			),
	],
	[
		ProductKeys3P.Stripe,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-stripe" */ './stripe').then(
				(m) => m.stripeConfig,
			),
	],
	[
		ProductKeys3P.AdobeXD,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-adobe-xd" */ './adobe-xd').then(
				(m) => m.adobeXDConfig,
			),
	],
	[
		ProductKeys3P.Aha,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-aha" */ './aha').then(
				(m) => m.ahaConfig,
			),
	],
	[
		ProductKeys3P.AdobeSign,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-adobe-sign" */ './adobe-sign').then(
				(m) => m.adobeSignConfig,
			),
	],
	[
		ProductKeys3P.Mural,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-mural" */ './mural').then(
				(m) => m.muralConfig,
			),
	],
	[
		ProductKeys3P.Webex,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-webex" */ './webex').then(
				(m) => m.webexConfig,
			),
	],
	[
		ProductKeys3P.Todoist,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-todoist" */ './todoist').then(
				(m) => m.todoistConfig,
			),
	],
	[
		ProductKeys3P.PagerDuty,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-pagerduty" */ './pagerduty').then(
				(m) => m.pagerDutyConfig,
			),
	],
	[
		ProductKeys3P.Pipedrive,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-pipedrive" */ './pipedrive').then(
				(m) => m.pipedriveConfig,
			),
	],
	[
		ProductKeys3P.Hubspot,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-hubspot" */ './hubspot').then(
				(m) => m.hubSpotConfig,
			),
	],
	[
		ProductKeys3P.Dovetail,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-dovetail" */ './dovetail').then(
				(m) => m.dovetailConfig,
			),
	],
	[
		ProductKeys3P.LaunchDarkly,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-launchdarkly" */ './launchdarkly'
			).then((m) => m.launchDarklyConfig),
	],
	[
		ProductKeys3P.Salesforce,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-salesforce" */ './salesforce').then(
				(m) => m.salesforceConfig,
			),
	],
	[
		ProductKeys3P.Sentry,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-sentry" */ './sentry').then(
				(m) => m.sentryConfig,
			),
	],
	[
		ProductKeys3P.Gitbook,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-gitbook" */ './gitbook').then(
				(m) => m.gitbookConfig,
			),
	],
	[
		ProductKeys3P.AWSS3,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-aws-s3" */ './aws-s3').then(
				(m) => m.awss3Config,
			),
	],
	[
		ProductKeys3P.Databricks,
		() =>
			import(/* webpackChunkName: "@atlassian/rovo-3p-config-databricks" */ './databricks').then(
				(m) => m.databricksConfig,
			),
	],
	[
		ProductKeys3P.ForgeAdamVitareqDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-adam-vitareq-dev" */ './forge-adam-vitareq-dev'
			).then((m) => m.forgeAdamVitareqConfig),
	],
	[
		ProductKeys3P.ForgeAdamVitareqProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-adam-vitareq-prod" */ './forge-adam-vitareq-prod'
			).then((m) => m.forgeAdamVitareqConfig),
	],
	[
		ProductKeys3P.ForgeK15tScrollSitesDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-k15t-scroll-sites-dev" */ './forge-k15t-scroll-sites-dev'
			).then((m) => m.forgeK15tScrollSitesConfig),
	],
	[
		ProductKeys3P.ForgeK15tScrollSitesProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-k15t-scroll-sites-prod" */ './forge-k15t-scroll-sites-prod'
			).then((m) => m.forgeK15tScrollSitesConfig),
	],
	[
		ProductKeys3P.ForgePioAwsDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-aws-dev" */ './forge-pio-aws-dev'
			).then((m) => m.forgePioAwsConfig),
	],
	[
		ProductKeys3P.ForgePioAwsProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-aws-prod" */ './forge-pio-aws-prod'
			).then((m) => m.forgePioAwsConfig),
	],
	[
		ProductKeys3P.ForgePioClickupDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-clickup-dev" */ './forge-pio-clickup-dev'
			).then((m) => m.forgePioClickupConfig),
	],
	[
		ProductKeys3P.ForgePioClickupProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-clickup-prod" */ './forge-pio-clickup-prod'
			).then((m) => m.forgePioClickupConfig),
	],
	[
		ProductKeys3P.ForgePioGitbookDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-gitbook-dev" */ './forge-pio-gitbook-dev'
			).then((m) => m.forgePioGitbookConfig),
	],
	[
		ProductKeys3P.ForgePioGitbookProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-gitbook-prod" */ './forge-pio-gitbook-prod'
			).then((m) => m.forgePioGitbookConfig),
	],
	[
		ProductKeys3P.ForgePioGithubDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-github-dev" */ './forge-pio-github-dev'
			).then((m) => m.forgePioGithubConfig),
	],
	[
		ProductKeys3P.ForgePioGithubProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-github-prod" */ './forge-pio-github-prod'
			).then((m) => m.forgePioGithubConfig),
	],
	[
		ProductKeys3P.ForgePioGitlabDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-gitlab-dev" */ './forge-pio-gitlab-dev'
			).then((m) => m.forgePioGitlabConfig),
	],
	[
		ProductKeys3P.ForgePioGitlabProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-gitlab-prod" */ './forge-pio-gitlab-prod'
			).then((m) => m.forgePioGitlabConfig),
	],
	[
		ProductKeys3P.ForgePioGoogleDriveDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-google-drive-dev" */ './forge-pio-google-drive-dev'
			).then((m) => m.forgePioGoogleDriveConfig),
	],
	[
		ProductKeys3P.ForgePioGoogleDriveProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-google-drive-prod" */ './forge-pio-google-drive-prod'
			).then((m) => m.forgePioGoogleDriveConfig),
	],
	[
		ProductKeys3P.ForgePioGuruDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-guru-dev" */ './forge-pio-guru-dev'
			).then((m) => m.forgePioGuruConfig),
	],
	[
		ProductKeys3P.ForgePioGuruProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-guru-prod" */ './forge-pio-guru-prod'
			).then((m) => m.forgePioGuruConfig),
	],
	[
		ProductKeys3P.ForgePioHelpjuiceDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-helpjuice-dev" */ './forge-pio-helpjuice-dev'
			).then((m) => m.forgePioHelpjuiceConfig),
	],
	[
		ProductKeys3P.ForgePioHelpjuiceProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-helpjuice-prod" */ './forge-pio-helpjuice-prod'
			).then((m) => m.forgePioHelpjuiceConfig),
	],
	[
		ProductKeys3P.ForgePioHubspotDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-hubspot-dev" */ './forge-pio-hubspot-dev'
			).then((m) => m.forgePioHubspotConfig),
	],
	[
		ProductKeys3P.ForgePioHubspotProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-hubspot-prod" */ './forge-pio-hubspot-prod'
			).then((m) => m.forgePioHubspotConfig),
	],
	[
		ProductKeys3P.ForgePioSalesforceDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-salesforce-dev" */ './forge-pio-salesforce-dev'
			).then((m) => m.forgePioSalesforceConfig),
	],
	[
		ProductKeys3P.ForgePioSalesforceProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-salesforce-prod" */ './forge-pio-salesforce-prod'
			).then((m) => m.forgePioSalesforceConfig),
	],
	[
		ProductKeys3P.ForgePioSharepointDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-sharepoint-dev" */ './forge-pio-sharepoint-dev'
			).then((m) => m.forgePioSharepointConfig),
	],
	[
		ProductKeys3P.ForgePioSharepointProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-sharepoint-prod" */ './forge-pio-sharepoint-prod'
			).then((m) => m.forgePioSharepointConfig),
	],
	[
		ProductKeys3P.ForgePioStackOverflowDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-stack-overflow-dev" */ './forge-pio-stack-overflow-dev'
			).then((m) => m.forgePioStackOverflowConfig),
	],
	[
		ProductKeys3P.ForgePioStackOverflowProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-pio-stack-overflow-prod" */ './forge-pio-stack-overflow-prod'
			).then((m) => m.forgePioStackOverflowConfig),
	],
	[
		ProductKeys3P.ForgeUtoolityGitbookDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-utoolity-gitbook-dev" */ './forge-utoolity-gitbook-dev'
			).then((m) => m.forgeUtoolityGitbookConfig),
	],
	[
		ProductKeys3P.ForgeUtoolityGitbookProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-utoolity-gitbook-prod" */ './forge-utoolity-gitbook-prod'
			).then((m) => m.forgeUtoolityGitbookConfig),
	],
	[
		ProductKeys3P.ForgeGdriveEugeneTestDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-gdrive-eugene-test-dev" */ './forge-gdrive-eugene-test-dev'
			).then((m) => m.forgeGdriveEugeneTestDevConfig),
	],
	[
		ProductKeys3P.ForgeGdriveEugeneTestProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-gdrive-eugene-test-prod" */ './forge-gdrive-eugene-test-prod'
			).then((m) => m.forgeGdriveEugeneTestProdConfig),
	],
	[
		ProductKeys3P.ForgeUnitoSalesforceDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-salesforce-dev" */ './forge-unito-salesforce-dev'
			).then((m) => m.forgeUnitoSalesforceConfig),
	],
	[
		ProductKeys3P.ForgeUnitoSalesforceProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-salesforce-prod" */ './forge-unito-salesforce-prod'
			).then((m) => m.forgeUnitoSalesforceConfig),
	],
	[
		ProductKeys3P.ForgeUnitoServicenowDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-servicenow-dev" */ './forge-unito-servicenow-dev'
			).then((m) => m.forgeUnitoServicenowConfig),
	],
	[
		ProductKeys3P.ForgeUnitoServicenowProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-servicenow-prod" */ './forge-unito-servicenow-prod'
			).then((m) => m.forgeUnitoServicenowConfig),
	],
	[
		ProductKeys3P.ForgeUnitoWrikeDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-wrike-dev" */ './forge-unito-wrike-dev'
			).then((m) => m.forgeUnitoWrikeConfig),
	],
	[
		ProductKeys3P.ForgeUnitoWrikeProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-wrike-prod" */ './forge-unito-wrike-prod'
			).then((m) => m.forgeUnitoWrikeConfig),
	],
	[
		ProductKeys3P.ForgeUnitoAsanaDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-asana-dev" */ './forge-unito-asana-dev'
			).then((m) => m.forgeUnitoAsanaConfig),
	],
	[
		ProductKeys3P.ForgeUnitoAsanaProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-asana-prod" */ './forge-unito-asana-prod'
			).then((m) => m.forgeUnitoAsanaConfig),
	],
	[
		ProductKeys3P.ForgeUnitoBasecampDev,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-basecamp-dev" */ './forge-unito-basecamp-dev'
			).then((m) => m.forgeUnitoBasecampConfig),
	],
	[
		ProductKeys3P.ForgeUnitoBasecampProd,
		() =>
			import(
				/* webpackChunkName: "@atlassian/rovo-3p-config-forge-unito-basecamp-prod" */ './forge-unito-basecamp-prod'
			).then((m) => m.forgeUnitoBasecampConfig),
	],
	// New 3P? Add it here
]);

const productKeys3PValues = new Set<string>(Object.values(ProductKeys3P));
export const isProductKey3P = (value: string): value is ProductKeys3P =>
	productKeys3PValues.has(value);
